import { useRef, useState, useEffect, useCallback } from 'react';
import {ThunkDispatch} from "@reduxjs/toolkit";
import {connect, useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import {roomDesignActions} from '../reducers/roomDesign'
import {selectCanChangePageSetting, selectCanAddFile, selectCanReloadRoomDesign, selectBeforeImageData, selectAfterImageData, selectAfterImageView, selectServiceSettings} from '../selectors'
import ExamplePhotoNG from './ExamplePhotoNG'
import RoomDesignEditRoomTypeLifestyle from './RoomDesignEditRoomTypeLifestyle'
import RoomDesignEditActionButtons from './RoomDesignEditActionButtons'
import {ReloadRoomDesignIcon, DelPhotoIcon, AddMorePhotoIcon, LoadingIcon,
  AfterImageDownloadIcon, AfterImageBackwardIcon, AfterImageForwardIcon,
  LeadPencilIcon} from './SVGIcons'
import { serviceSettingsActions, WATERMARK_TYPE } from '../reducers/serviceSettings'
import {toastActions} from '../reducers/toast'
import MyPageModal from './mypage/MyPageModal'
import { Tooltip } from 'primereact/tooltip'
import { InfoIcon } from './SVGIcons'
import { isWatermarkEnabled, isAnnotationEnabled, fetchImageAsBlob } from '../utils/watermark'
import { reapplyWatermarksOnSettingsChange } from '../utils/watermarkHelpers'
import { useServiceSettings } from '../hooks/useServiceSettings'
import { useWatermarkSettings } from '../hooks/useWatermarkSettings'

function mapStateToProps(state: any) {
  const canChangePageSetting = selectCanChangePageSetting(state)
  const canAddFile = selectCanAddFile(state)
  const canReloadRoomDesign = selectCanReloadRoomDesign(state)
  const selectedBeforeImageData = selectBeforeImageData(state)
  const afterImageView = selectAfterImageView(state)
  const {selectedAfterImages, selectedAfterImageData, selectedAfterImageIndex} = selectAfterImageData(state)
  const watermarkSettings = selectServiceSettings(state)
  
  // ウォーターマークまたは注釈が有効かチェック
  const hasWatermarkSettings = watermarkSettings && (isWatermarkEnabled(watermarkSettings) || isAnnotationEnabled(watermarkSettings))

  return { ...state.roomDesign,
    canChangePageSetting,
    canAddFile,
    canReloadRoomDesign,
    selectedBeforeImageData,
    selectedAfterImages,
    selectedAfterImageData,
    selectedAfterImageIndex,
    afterImageView,
    serviceSettings: state.serviceSettings,
    watermarkSettings,
    hasWatermarkSettings
  }
}

const watermarkTooltip = '・「アカウント設定」で設定したロゴを画像上に表示する機能。\n・設定を変更する場合は、「設定はこちら」をクリック。';

const RoomDesignEditFormSome = (props: any) => {
  let {
    beforeImages,
    maxExecTimePerImage,
    runTime,
    canChangePageSetting,
    canAddFile,
    canReloadRoomDesign,
    selectedBeforeImageData,
    selectedAfterImages,
    selectedAfterImageData,
    selectedAfterImageIndex,
    afterImageView,
    serviceSettings,
    watermarkGlobalEnabled,
    processingWatermarks,
    watermarkSettings,
    hasWatermarkSettings,
    isEditPhotoPopupShow,
    generatingImages
  } = props
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>()
  const navigate = useNavigate()
  const inputFile = useRef<HTMLInputElement | null>(null);
  const beforeImageRef = useRef<HTMLImageElement | null>(null);
  const afterImageRef = useRef<HTMLImageElement | null>(null);
  const [afterImageLoaded, setAfterImageLoaded] = useState(false);
  
  // 画像のURLを計算（watermark有無を含む）
  const displayImageUrl = watermarkGlobalEnabled && selectedAfterImageData?.watermarkedURI 
    ? selectedAfterImageData.watermarkedURI 
    : selectedAfterImageData?.serverURI;
  
  // マイページモーダルの表示状態
  const [showMyPageModal, setShowMyPageModal] = useState(false)
  
  // カスタムフックを使用
  const { lastProcessedTimestamp, setLastProcessedTimestamp } = useServiceSettings();
  
  // watermarkedURIまたはselectedAfterImageDataが変更されたら画像を再読み込み
  useEffect(() => {
    if (selectedAfterImageData) {
      setAfterImageLoaded(false);
    }
  }, [selectedAfterImageData?.watermarkedURI, selectedAfterImageData?.id]);
  
  // serviceSettingsのlastSavedTimestampを監視して、設定保存を検知
  const savedTimestamp = serviceSettings?.lastSavedTimestamp;
  
  // カスタムフックでウォーターマーク設定を管理
  const { handleWatermarkToggle } = useWatermarkSettings({
    savedTimestamp,
    lastProcessedTimestamp,
    setLastProcessedTimestamp,
    watermarkGlobalEnabled,
    selectedAfterImages,
    watermarkSettings,
    hasWatermarkSettings,
    clearAllWatermarksAction: roomDesignActions.clearAllWatermarks,
    applyWatermarkToImageAction: roomDesignActions.applyWatermarkToImage,
    setWatermarkGlobalEnabledAction: roomDesignActions.setWatermarkGlobalEnabled,
    toggleAllWatermarksAction: roomDesignActions.toggleAllWatermarks,
  });
  
  // 新規作成時にデフォルト値を適用
  useEffect(() => {
    if (beforeImages.length === 1 && selectedBeforeImageData && serviceSettings && !selectedBeforeImageData.roomType && !selectedBeforeImageData.lifestyle) {
      // 部屋タイプのデフォルト値を適用
      if (serviceSettings.default_room_type) {
        dispatch(roomDesignActions.setRoomType(serviceSettings.default_room_type));
      }
      // 生活スタイルのデフォルト値を適用（oneRoom以外の場合のみ）
      if (serviceSettings.default_life_style && serviceSettings.default_room_type !== 'oneRoom') {
        dispatch(roomDesignActions.setLifestyle(serviceSettings.default_life_style));
      }
    }
  }, [beforeImages.length, selectedBeforeImageData, serviceSettings, dispatch]);
  
  useEffect(() => {
    setAfterImageLoaded(false)
    // 編集画面が開いている場合はafterImageRefを更新しない
    if (!isEditPhotoPopupShow) {
      dispatch(roomDesignActions.setAfterImageRef(afterImageRef.current))
      // 編集画面を閉じた後、img要素が既に読み込まれている場合はafterImageLoadedをtrueに設定
      if (afterImageRef.current && afterImageRef.current.complete) {
        setAfterImageLoaded(true)
      }
    }
  }, [selectedBeforeImageData, selectedAfterImageIndex, isEditPhotoPopupShow]);
  
  
  const maxExecTime = maxExecTimePerImage * beforeImages.length
  const processPercent = runTime < maxExecTime*0.9 ? runTime / maxExecTime : 0.9
  const displayPercent = Math.floor(processPercent * 100) + "%"
  const strokeDashoffset = 100 - 100*processPercent
  
  
  // ウォーターマークトグルのハンドラー（モーダル表示を含む）
  const handleWatermarkToggleWithModal = useCallback((enabled: boolean) => {
    handleWatermarkToggle(enabled, () => setShowMyPageModal(true));
  }, [handleWatermarkToggle]);
  
  // 画像ダウンロードのハンドラー（ウォーターマーク対応）
  const handleDownloadImage = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault()
    if (!selectedAfterImageData) return

    try {
      const imageUrl = watermarkGlobalEnabled && selectedAfterImageData.watermarkedURI
        ? selectedAfterImageData.watermarkedURI
        : selectedAfterImageData.serverURI

      // クロスオリジンURLでもダウンロードできるようにBlobに変換
      const blob = await fetchImageAsBlob(imageUrl)
      const blobUrl = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = blobUrl
      a.download = selectedAfterImageData.fileName || 'image.jpg'
      a.click()

      // メモリリーク防止
      URL.revokeObjectURL(blobUrl)

      dispatch(toastActions.toastSuccess('画像のダウンロードを開始しました'))
    } catch (error) {
      console.error('Download failed:', error)
      dispatch(toastActions.toastError('画像のダウンロードに失敗しました'))
    }
  }, [selectedAfterImageData, watermarkGlobalEnabled, dispatch])

  const onButtonAddImage = () => {
    if (!inputFile || !inputFile.current) return

    inputFile.current.click();
  }
  const addBeforeImages = (event: any) => {
    if (!canChangePageSetting) return

    dispatch(roomDesignActions.addBeforeImages(event.target.files))
  }
  const handleDragOver = (e: any) => {
    e.preventDefault();
  };
  const handleDragLeave = (e: any) => {
    e.preventDefault();
  };
  const handleDrop = (e: any) => {
    e.preventDefault();
    if (!canChangePageSetting) return

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFiles = e.dataTransfer.files
      dispatch(roomDesignActions.addBeforeImages(droppedFiles))
    }
  };
  const removeBeforeImage = (stubId: number) => {
    if (!canChangePageSetting) return
    
    dispatch(roomDesignActions.removeBeforeImage(stubId))
  }
  const setSelectedBeforeImage = (stubId: number) => {
    // 生成完了状態の場合は進捗表示をクリア
    const progress = generatingImages[stubId]
    if (progress && progress.current === progress.total) {
      dispatch(roomDesignActions.clearGenerationProgress(stubId))
    }
    dispatch(roomDesignActions.setSelectedBeforeImage(stubId))
  }
  const setSelectedAfterImage = useCallback(async (id: number) => {
    dispatch(roomDesignActions.setSelectedAfterImage(id))
    
    // ウォーターマークが有効で、選択した画像にまだ適用されていない場合
    if (watermarkGlobalEnabled) {
      const image = selectedAfterImages.find((img: any) => img.id === id)
      if (image && !image.watermarkedURI && !image.isProcessingWatermark) {
        await dispatch(roomDesignActions.applyWatermarkToImage({
          imageId: image.id,
          imageUrl: image.serverURI
        }))
      }
    }
  }, [dispatch, watermarkGlobalEnabled, selectedAfterImages])
  const selectPrevAfterImage = () => {
    if (selectedAfterImages[selectedAfterImageIndex-1])
      dispatch(roomDesignActions.setSelectedAfterImage(selectedAfterImages[selectedAfterImageIndex-1].id))
  }
  const selectNextAfterImage = () => {
    if (selectedAfterImages[selectedAfterImageIndex+1])
      dispatch(roomDesignActions.setSelectedAfterImage(selectedAfterImages[selectedAfterImageIndex+1].id))
  }
  const editImage = () => {
    if (beforeImageRef && afterImageRef) {
      // 編集画面用にオリジナル画像（ウォーターマークなし）を別途ロード
      const originalImage = new Image()
      originalImage.crossOrigin = 'anonymous'
      originalImage.src = selectedAfterImageData?.serverURI || ''
      originalImage.onload = () => {
        dispatch(roomDesignActions.setBeforeImageRef(beforeImageRef.current))
        dispatch(roomDesignActions.setAfterImageRef(originalImage))
        dispatch(roomDesignActions.setIsEditPhotoPopupShow(true))
      }
    }
  }
  const handeleAfterImageLoaded = () => {
    setAfterImageLoaded(true)
    // 編集画面が開いている場合はafterImageRefを更新しない
    if (!isEditPhotoPopupShow) {
      dispatch(roomDesignActions.setAfterImageRef(afterImageRef.current))
    }
  }
  const reloadPage = () => {
    if(!canReloadRoomDesign) return

    dispatch(roomDesignActions.setInitState())
  }

  return (
    <>
      <style>{`
        .orange-tooltip .p-tooltip-text {
          background-color: rgb(249 115 22);
          color: white;
          white-space: pre-line;
        }
        .orange-tooltip .p-tooltip-arrow {
          border-right-color: rgb(249 115 22);
        }
      `}</style>
      <Tooltip target=".tooltip-watermark-room" className="orange-tooltip max-w-xs" />
      <div className="max-w-8xl w-full mx-auto pt-1 pb-3 px-4 sm:px-6 md:px-8 text-[#2d2d2d]">
        <div className="grid grid-cols-9 gap-2">
          <div className="col-1 bg-gray-200 overflow-y-auto rounded min-h-[30.28rem] flex flex-col p-2">
            <button type="button" onClick={reloadPage} disabled={!canReloadRoomDesign}
              className={canReloadRoomDesign
                ? "bg-gray-50 flex p-2 rounded-md border border-yellow-50 mb-4 text-sm text-yellow-700 items-center justify-center"
                : "bg-gray-300 flex p-2 rounded-md mb-4 text-sm items-center justify-center"
              }>
              <ReloadRoomDesignIcon />一括削除
            </button>
            <ul className="space-y-2">
              {beforeImages.map((beforeImage: any) => {
                const progress = generatingImages[beforeImage.stubId];
                const isGenerating = progress !== undefined;

                return (
                  <li key={beforeImage.stubId} onClick={e => setSelectedBeforeImage(beforeImage.stubId)}
                    className={beforeImage.stubId == selectedBeforeImageData.stubId
                      ? "flex items-center justify-center w-full bg-gray-300 h-24 w-28 overflow-hidden mx-auto rounded-sm cursor-pointer relative ring-offset-2 ring-2 ring-blue-500"
                      : "flex items-center justify-center w-full bg-gray-300 h-24 w-28 overflow-hidden mx-auto rounded-sm cursor-pointer relative"}>
                    {beforeImage.roomType && (beforeImage.roomType === 'oneRoom' || beforeImage.lifestyle) && !isGenerating && (
                      <span className="bg-[#ff4600cc] absolute top-1 left-1 text-xs text-white rounded-lg px-1">選択済</span>
                    )}
                    {isGenerating && (
                      <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center">
                        {progress.current === progress.total ? (
                          <span className="bg-[#ff4600cc] text-white text-sm font-bold rounded-lg px-3 py-1.5">生成完了</span>
                        ) : (
                          <div className="bg-[#ff4600cc] rounded-lg px-3 py-2">
                            <p className="text-white text-xs font-bold text-center mb-0.5">生成中</p>
                            <p className="text-white text-sm font-bold text-center">{progress.current}/{progress.total}枚</p>
                          </div>
                        )}
                      </div>
                    )}
                    {canChangePageSetting && !isGenerating && (
                      <button type="button" onClick={e => removeBeforeImage(beforeImage.stubId)} className="absolute top-1 right-1 opacity-50 hover:opacity-70">
                        <DelPhotoIcon />
                      </button>
                    )}
                    <img src={beforeImage.uri} className="block w-full" alt=""/>
                  </li>
                );
              })}

              {canAddFile && (
                <li className="text-center">
                  <label>
                    <button type="button" onClick={onButtonAddImage} className="text-blue-600 hover:text-blue-600 rounded-full hover:bg-gray-100">
                      <AddMorePhotoIcon />
                    </button>
                    <input type="file" ref={inputFile} multiple accept="image/png, image/jpeg, image/webp" onChange={addBeforeImages} className="hidden" />
                  </label>
                </li>
              )}
            </ul>
          </div>

          <div
            className="grid grid-cols-2 col-span-8 border border-gray-300 bg-white overflow-y-auto rounded">
            <div className="flex flex-col w-full col-1 flex-nowrap p-3 bg-white">
              <RoomDesignEditRoomTypeLifestyle />
              <div
                className="flex flex-col items-center justify-center h-full rounded">
                {selectedBeforeImageData && canAddFile && (
                  <label onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
                    <img src={selectedBeforeImageData.uri} className="block w-full" alt=""/>
                    <input type="file" multiple accept="image/png, image/jpeg, image/webp" onChange={addBeforeImages} className="hidden" />
                  </label>
                )}
                {selectedBeforeImageData && !canAddFile && (
                  <img ref={beforeImageRef} src={selectedBeforeImageData.uri} className="block w-full" alt=""/>
                )}
              </div>
            </div>

            {afterImageView == 'show_blank' && (
              <ExamplePhotoNG />
            )}
            {afterImageView == 'show_gen_image_error' && (
              <div className="p-3 col-1 border-s border-gray-300 flex">
                <div className="relative m-auto text-red-600 text-center text-lg">
                  画像生成に失敗しました。<br />
                  再度画像生成を行ってください。<br />
                  ※クレジットは消費されておりません。
                </div>
              </div>
            )}
            {afterImageView == 'show_images' && (
              <div className="p-3 col-1 border-s border-gray-300 flex">
                <div className="relative w-full after_image_region flex flex-col justify-between">
                  <div className="flex mb-2 mt-10 relative z-30 justify-between">
                    <div className="grid grid-cols-9 gap-1">
                      {selectedAfterImages.map((image: any, index: number) => (
                        <button key={image.id} onClick={() => setSelectedAfterImage(image.id)} className={selectedAfterImageData && selectedAfterImageData.id == image.id
                          ? "min-w-6 min-h-6 submit-btn text-white hover:text-gray-400 rounded"
                          : "min-w-6 min-h-6 bg-gray-800 text-white hover:text-gray-400 rounded"
                        }>{index+1}</button>
                      ))}
                    </div>
                    {/* ウォーターマークトグル */}
                    <div className="flex items-center">
                      <label className="flex items-center bg-white/90 rounded-lg px-3 py-1 shadow-sm cursor-pointer hover:bg-white/95 transition-colors">
                        <input
                          type="checkbox"
                          checked={watermarkGlobalEnabled}
                          onChange={(e) => handleWatermarkToggleWithModal(e.target.checked)}
                          disabled={!hasWatermarkSettings || processingWatermarks.length > 0}
                          className="mr-2 w-4 h-4"
                          style={{ accentColor: '#ff4500' }}
                        />
                        <span className="text-sm font-medium">ウォーターマーク</span>
                      </label>
                      <InfoIcon
                        className="tooltip-watermark-room ml-1 mr-2 w-4 h-4 cursor-pointer text-gray-400"
                        data-pr-tooltip={watermarkTooltip}
                        data-pr-position="left"
                      />
                      
                      <span className="ml-2 text-xs">
                        設定は<button
                          onClick={() => setShowMyPageModal(true)}
                          className="text-blue-600 hover:text-blue-800 underline"
                        >こちら</button>
                      </span>
                    </div>
                  </div>
                  <div className="relative group">
                    {selectedAfterImageData && (
                      <div className="duration-100 ">
                        <div className='absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 z-30 flex items-center gap-8'>
                          <button onClick={editImage} className='w-[72px] h-[72px] rounded-full bg-white/95 hover:bg-white/90 hidden group-hover:flex-col justify-center items-center z-30'>
                            <LeadPencilIcon className="w-[35px] h-[35px] text-gray-700" />
                            <p className='text-sm text-center'>編集</p>
                          </button>
                          <button onClick={handleDownloadImage} className="w-[72px] h-[72px] rounded-full bg-white/95 hover:bg-white/90 hidden group-hover:flex-col justify-center items-center z-30">
                            <AfterImageDownloadIcon />
                            <p className='text-sm text-center'>保存</p>
                          </button>
                        </div>
                        <span className="absolute group-hover:inset-0 group-hover:bg-gray-500/60 z-10"></span>
                          <img 
                            ref={afterImageRef} 
                            src={displayImageUrl} 
                            style={{display: afterImageLoaded ? 'block': 'none'}} 
                            className="block w-full" 
                            alt=""
                            onLoad={handeleAfterImageLoaded} 
                            crossOrigin={'anonymous'}/>
                      </div>
                    )}
                    {selectedAfterImages[selectedAfterImageIndex-1] && (
                      <button onClick={selectPrevAfterImage}
                        className="absolute -translate-y-1/2 top-1/2 start-0 z-30 flex px-4 cursor-pointer focus:outline-none hidden group-hover:block">
                        <span
                          className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white/95 hover:bg-white/90 group-focus:ring-4 group-focus:ring-white group-focus:outline-none border border-gray-400">
                          <AfterImageBackwardIcon />
                          <span className="sr-only">前へ</span>
                        </span>
                      </button>
                    )}
                    {selectedAfterImages[selectedAfterImageIndex+1] && (
                      <button onClick={selectNextAfterImage}
                        className="absolute -translate-y-1/2 top-1/2 end-0 z-30 flex px-4 cursor-pointer focus:outline-none hidden group-hover:block">
                        <span
                          className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white/95 hover:bg-white/90 group-focus:ring-4 group-focus:ring-white group-focus:outline-none border border-gray-400">
                          <AfterImageForwardIcon />
                          <span className="sr-only">次へ</span>
                        </span>
                      </button>
                    )}
                  </div>

                  {!afterImageLoaded && (
                    <LoadingIcon className="absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2" />
                  )}
                </div>
              </div>
            )}
            {afterImageView == 'show_loading' && (
              <div className="p-3 col-1 border-s border-gray-300 flex">
                <div className="relative size-40 m-auto">
                  <svg className="size-full -rotate-90" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="18" cy="18" r="16" fill="none" className="stroke-current text-gray-200"
                      strokeWidth="2"></circle>
                    <circle cx="18" cy="18" r="16" fill="none" className="stroke-current text-blue-600"
                      strokeWidth="2" strokeDasharray="100" strokeDashoffset={strokeDashoffset} strokeLinecap="round"></circle>
                  </svg>
                  <div className="absolute top-1/2 start-1/2 transform -translate-y-1/2 -translate-x-1/2">
                    <span className="text-center text-2xl font-bold text-blue-600">{displayPercent}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <RoomDesignEditActionButtons />
      </div>
      
      {/* マイページモーダル */}
      {showMyPageModal && (
        <MyPageModal 
          initialTab="service"
          initialExpandWatermark={true}
          onClose={() => {
            setShowMyPageModal(false)
            // モーダルを閉じた後、ServiceSettingsを再取得
            dispatch(serviceSettingsActions.fetchServiceSettings())
          }} 
        />
      )}
    </>
  );
};

export default connect(mapStateToProps)(RoomDesignEditFormSome)